// ── QZ Tray integration for silent printing ──────────────────────────────────
// Connects to locally-installed QZ Tray via WebSocket.
// Supports: Zebra (raw ZPL) and Brother QL (pixel/HTML) printers.

import qz from 'qz-tray';
import { KEYUTIL, KJUR, hextob64 } from 'jsrsasign';
import JsBarcode from 'jsbarcode';

// ── Certificate & signing (self-signed, internal use) ─────────────────────────

const CERTIFICATE = `-----BEGIN CERTIFICATE-----
MIIDEzCCAfugAwIBAgIUVN93r2qFGH14KKfuNqW1M5rNCLcwDQYJKoZIhvcNAQEL
BQAwGTEXMBUGA1UEAwwOQU9JIFBDIEJ1aWxkZXIwHhcNMjYwNDAyMTQ1ODUxWhcN
MzYwMzMwMTQ1ODUxWjAZMRcwFQYDVQQDDA5BT0kgUEMgQnVpbGRlcjCCASIwDQYJ
KoZIhvcNAQEBBQADggEPADCCAQoCggEBAIY8mdUJhDGU150Am+yPV2N4vJhxsHuH
RuLvvfn/eNRm52IB5/qGcZbFsN5+t+bJTjpnptt4Ohnu5+kCQPj6XPEiOn873ULZ
MOX794dpX87tQwUi327TaGqlJ00GmpeDpxDK5cCFeoIbqwMrc7yQTaXJ0QiEifeK
VlYiiTgOdBk/TnwgGMZE7lGMvZ/OMg6kW6fmnEeZdeuG7BcA1wjq6uwz/K/zIExz
oJcpbEoa/JcZ6Donqg9aUlUfTBiddyB1t2Emr9gjTQBlptb997glAUJ8pFszGhv+
ZT20xKGUGmLiFeDwh+HyfYW47B6p8cMLd64wR83Le90gzBrRnRgzY4kCAwEAAaNT
MFEwHQYDVR0OBBYEFEltX5cbCyXAJP5KWPLuP2SvaqA+MB8GA1UdIwQYMBaAFElt
X5cbCyXAJP5KWPLuP2SvaqA+MA8GA1UdEwEB/wQFMAMBAf8wDQYJKoZIhvcNAQEL
BQADggEBABIQJJKSoNUMw5/ZUc+c7ScFMSeylqslLUCumUE7xzNTFFrxppbFfMPZ
nJB+gvrIU2Y0k9pPJN++6HrqmYt9cDZtFEC7HM/vw51MgsW9vUZeIQrYSG9/TtFx
KVMJLq1Ab/vO/ufGu3MdLx82G+pXMLaLqTqxuoAHAz0hD8xVJgx/lW76Iyj45Ihj
UGVG76r3HMyOUxc5mtN7WsdMRzVJRYkolCDGfVcfnAcyDwp9tRoU99XZKAn7FGHo
9AwFqTTh+8fT/pgtkBdfceVpQMj7wp6z8/2oj4NGLF5GFJLPw3pujm/tujte3S40
h/QPOQOvxEDGrzBBPVaXiJkY/XaaDk4=
-----END CERTIFICATE-----`;

const PRIVATE_KEY = `-----BEGIN PRIVATE KEY-----
MIIEugIBADANBgkqhkiG9w0BAQEFAASCBKQwggSgAgEAAoIBAQCGPJnVCYQxlNed
AJvsj1djeLyYcbB7h0bi7735/3jUZudiAef6hnGWxbDefrfmyU46Z6bbeDoZ7ufp
AkD4+lzxIjp/O91C2TDl+/eHaV/O7UMFIt9u02hqpSdNBpqXg6cQyuXAhXqCG6sD
K3O8kE2lydEIhIn3ilZWIok4DnQZP058IBjGRO5RjL2fzjIOpFun5pxHmXXrhuwX
ANcI6ursM/yv8yBMc6CXKWxKGvyXGeg6J6oPWlJVH0wYnXcgdbdhJq/YI00AZabW
/fe4JQFCfKRbMxob/mU9tMShlBpi4hXg8Ifh8n2FuOweqfHDC3euMEfNy3vdIMwa
0Z0YM2OJAgMBAAECgf9GT4cGY5wQACZBW1BQizS9SUBMUUBKz8tZDXJI/WDZebr6
9HApu/UFvufoCNjpOd7hBW7ETZjFC/oBV0PpOgbBW1fdAB970kxw4ndwNefSoWRx
pd8+aET0eF7imsirPNbcBsantvCA/V5LcSKR0pMrHP2qhUa+tmywgw9hwG3Cbrsl
LM/+pChcATD9xMH7h72FWD9XDw0Bm78Kk6GK1S7bBbICPbxTXsbyGM+Zha0XC26d
keQTCVjReSsfyeTgBG5A56iQ+Mh9v1351kNITJmFaAnZdxIcI0+osS0s1DZfQ9Fu
WoyXax6vyz6yVL4Yp9dHUWv7v6k6eKtxxAJT/qECgYEAvWilEn9frSScmWbbGq6O
233gK+RflWRQbDcDaYmAmSRBXm4NjWzxkk6gHRhyqFdyu1JOyP8ATGOUr94n3gnR
6i9+M+Tel4XAdRjN6+hCxcZSyTYJJkyAIrt/1E5Mf7xg2RPdzlmpn24dOtC13sVZ
twrDKwYbfgI54pnlvB5ffhkCgYEAtW5Omkaj5j2ji1ggVyOpql+pwJUeq3Z4lwih
ZseEbTgQHPKdpaVwaOZvisQy+wxaLLvHpW66A3i6nOuirULfZQwh4lsH8PGfhv/p
ma7teStk6ETeRcywcx812EZwlF3/962HwY9fR1DMrcCgKv6JrzMcqNCGplNO9xrd
w28U3vECgYA6TJdilioQ1RAOd4ZBNDFshdYWLHjvNq06YcR/G3+lo28rbZxmR34j
lmfaDMhd4GzEE5WEcFjbKlt9tld3FHa0KmIh/vQ0l0qhwDzBu/+xeFXK8QLn8uwN
xMCXzHHnb0gwCNlgxmBbXj1I+SzBoOT4XiusdnjH1E+U2+HGgWLLIQKBgDRVVSb7
KVvIe9w911ZLWy1h2r1KRkZ/LdmjC6tx8ajvevZepUqapgocLiCA55OyxHAS2kvF
3LITqQCKl6oSmA21qjB4wZmmmLWALAnAg49oDPtSoirQGDWc0xNHfkHNu3/d9kLG
8FzJ2cBkx86fDWgT/jhTrgNcFHMCtOUirYqBAoGAXw1sLtd/Ni2E9G3rV+Z9W5cQ
s1AdJK4btPm2gdoN8ShYV05/paIO8ZNPwYiP+hpXllsq5zbcYB0SQdUomitkVcUW
0XtyZnU7LeRex2N706TRcSSI5qh67iW/txZMraQzUiMDACJXr9oUk+sdMGnNFCYo
LizaGz4JDgKqS0oR6Jc=
-----END PRIVATE KEY-----`;

let signingConfigured = false;

function setupSigning() {
  if (signingConfigured) return;
  qz.security.setCertificatePromise((resolve) => resolve(CERTIFICATE));
  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise((toSign) => {
    return (resolve, reject) => {
      try {
        const pk = KEYUTIL.getKey(PRIVATE_KEY);
        const sig = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' });
        sig.init(pk);
        sig.updateString(toSign);
        resolve(hextob64(sig.sign()));
      } catch (err) {
        reject(err);
      }
    };
  });
  signingConfigured = true;
}

// ── Connection management ─────────────────────────────────────────────────────

let connecting = false;

export async function connectQz(): Promise<void> {
  if (qz.websocket.isActive() || connecting) return;
  connecting = true;
  try {
    setupSigning();
    await qz.websocket.connect({ retries: 3, delay: 1 });
  } finally {
    connecting = false;
  }
}

export function isQzConnected(): boolean {
  return qz.websocket.isActive();
}

export async function disconnectQz(): Promise<void> {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
  }
}

// ── Printer discovery ─────────────────────────────────────────────────────────

export async function listPrinters(): Promise<string[]> {
  await connectQz();
  const result = await qz.printers.find();
  return Array.isArray(result) ? result : [result];
}

// ── Print functions ───────────────────────────────────────────────────────────

/** Print raw ZPL to a Zebra printer by name */
export async function printZpl(printerName: string, zpl: string): Promise<void> {
  await connectQz();
  const config = qz.configs.create(printerName);
  await qz.print(config, [zpl]);
}

/** Print HTML to a Brother QL printer with a specific paper size */
export async function printHtmlLabel(
  printerName: string,
  html: string,
): Promise<void> {
  await connectQz();
  const config = qz.configs.create(printerName, {
    units: 'mm',
    size: { width: 62, height: 29 },
    margins: { top: 0, right: 0, bottom: 0, left: 0 },
    colorType: 'grayscale',
    scaleContent: true,
  });
  await qz.print(config, [{
    type: 'pixel',
    format: 'html',
    flavor: 'plain',
    data: html,
  }]);
}

/** Print multiple HTML labels to Brother QL */
export async function printHtmlLabels(
  printerName: string,
  htmlLabels: string[],
): Promise<void> {
  await connectQz();
  const config = qz.configs.create(printerName, {
    units: 'mm',
    size: { width: 62, height: 29 },
    margins: { top: 0, right: 0, bottom: 0, left: 0 },
    colorType: 'grayscale',
    scaleContent: true,
  });
  const data = htmlLabels.map(html => ({
    type: 'pixel' as const,
    format: 'html' as const,
    flavor: 'plain' as const,
    data: html,
  }));
  await qz.print(config, data);
}

// ── Brother 62mm label HTML generators ────────────────────────────────────────
// 62mm roll, ~54mm usable width, variable height

/**
 * Code 128 barcode, value printed underneath, as a PNG data URI.
 *
 * Drawn here rather than fetched: these labels used to load their barcode from
 * barcodeapi.org at print time, and when that domain stopped resolving every
 * label came out with an empty box where the barcode should be. Drawn large so
 * the CSS scales it down — scaling a small image up blurs the bars.
 */
function barcodeDataUri(value: string): string {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 4,
    height: 160,
    fontSize: 36,
    margin: 0,
    displayValue: true,
  });
  return canvas.toDataURL('image/png');
}

/** Returns a single label HTML: description at top, barcode with SKU text below */
export function skuLabelHtml62mm(sku: string, description: string): string[] {
  const desc = description
    ? (description.length > 80 ? description.slice(0, 77) + '...' : description)
    : '';
  // Two lines at 8pt fit ~80 characters across the roll; one line at 10pt cut a
  // typical spec string off after the storage. The fixed height clips anything
  // that would wrap to a third line rather than pushing the barcode off the label.
  return [`<div style="width:62mm;height:29mm;padding:1mm 2mm;box-sizing:border-box;font-family:Segoe UI,Arial,sans-serif;overflow:hidden;">
    <div style="font-size:8pt;line-height:1.15;height:7.4mm;color:#333;overflow:hidden;margin-bottom:0.5mm;">${desc}</div>
    <div style="text-align:center;">
      <img src="${barcodeDataUri(sku)}"
           style="width:56mm;height:18mm;" />
    </div>
  </div>`];
}

export function snLabelHtml62mm(serialNumber: string): string {
  return `<div style="width:62mm;height:29mm;padding:1mm 2mm;box-sizing:border-box;font-family:Segoe UI,Arial,sans-serif;overflow:hidden;">
    <div style="margin-bottom:0.5mm;">
      <span style="font-size:9pt;font-weight:bold;">Serial Number:</span>
    </div>
    <div style="text-align:center;">
      <img src="${barcodeDataUri(serialNumber)}"
           style="width:56mm;height:18mm;" />
    </div>
  </div>`;
}

// ── Brother 103x164mm certificate HTML helpers (if needed via QZ) ─────────────

// Paper names matching the Brother QL-1100 driver
export const BROTHER_62X29_PAPER = '62mm x 29mm';
export const BROTHER_103X164_PAPER = '103mm x 164mm';
